/**
 * The tenant alert engine's codes about itself: whether it covered the fleet,
 * whether a region was seen at all, and whether a detector could look.
 *
 * Fingerprints: the two fleet codes are PLATFORM scope, entity `vibe_fleet`,
 * entityId `fleet`. `VIBE_ALERT_DETECTOR_BLIND` is per tenant (ORGANIZATION
 * scope), entity `vibe_alert_detector`, entityId `<detector>:<organizationId>` —
 * one row per detector per tenant, so two detectors blind on one tenant are two
 * rows and one detector blind on two tenants is two rows.
 */
import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

export const VIBE_ALERT_ENGINE_ISSUES = {
  /**
   * Paging: an alerter covering less than it claims is the defect every other
   * row on this page depends on not having — a skipped tenant is a tenant no
   * code here can fire for.
   */
  VIBE_ALERT_COVERAGE: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Vibe tenant alert engine did not cover the whole fleet on its last tick: the tenant list could not be read, came back empty or below its floor (`tenantPopulationFloor`), or a detector skipped a tenant. Until this clears, a tenant's own alerts may be missing because nothing looked. The payload says which.",
    severity: "high",
    page: true,
    remedy:
      "Read the census on the payload. A failed tenant list is the database read the engine makes every tick — check the worker log for its error. A population below its floor means tenants left the list: if the fleet really shrank, lower `vibe.tenant_alerts.policy` → `tenantPopulationFloor` to the observed size. A detector that skipped a tenant is a bug in that detector. This resolves on its own on the first complete census."
  },

  /**
   * Paging: every tenant in a region unobserved at once is a shared cause — a
   * lost EC2 grant, a broken route home — that a pile of per-tenant rows would
   * hide, and it leaves that whole region's nodes unwatched.
   */
  VIBE_ALERT_NODE_HEALTH_REGION_CENSUS: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The node-health sweep saw no tenant cluster at all in one or more AWS regions for longer than the unobserved window, or its region census could not be taken. A whole region going dark at once is a cause the region shares — its EC2 read or its agents' route home — not any one tenant's problem. The payload names the regions.",
    severity: "high",
    page: true,
    remedy:
      "For each region on the payload, check the control plane's EC2 access there (an `UnauthorizedOperation` on every tenant is a lost grant) and whether the region's agents are reaching the control plane at all. A refused census means the sweep listed no serving cluster — check the sweep's own log. This resolves on its own once every region serving a tenant is seen."
  },

  /**
   * Not paging: a detector that cannot read its signal on one tenant is that
   * tenant's reading going dark, and the fleet-wide shapes of that — a region,
   * the prober, the census — page through their own codes.
   */
  VIBE_ALERT_DETECTOR_BLIND: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "One of the Vibe tenant alert engine's detectors could not read its signal for this tenant for longer than the policy's blind window, so the condition it watches is not being watched here. Any alert that detector already had open for this tenant is held, not resolved. The payload names the detector and carries the code and reason of the read that failed.",
    severity: "medium",
    remedy:
      "Read the detector and the code on the payload — the code is the provider's own where it gave one (`UnauthorizedOperation`, an HTTP status) and the engine's where it did not (`DETECTOR_THREW` is a bug in that detector, with its error). Fix the read; this resolves on its own on the first tick the detector reads its signal again."
  }
} as const satisfies Record<string, IssueDefinition>;
